import React, { useEffect, useState } from "react";
import { IonLoading } from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { auth, isFirebaseConfigured, usingEmulators } from "../firebase";
import { track } from "../services/analytics";
import { isPasswordPwned, passwordIssues, upgradeGuestWithEmail } from "../services/authProviders";
import AuthShell, { inputClass, labelClass } from "./AuthShell";
import { friendlyAuthError } from "./Login";

export const PasswordStrength: React.FC<{ password: string }> = ({ password }) => {
  const { t } = useTranslation();
  if (!password) return null;
  const issues = passwordIssues(password);
  const score = Math.max(0, 4 - issues.length) + (password.length >= 12 ? 1 : 0);
  const colors = ["bg-red-500", "bg-red-500", "bg-accent-500", "bg-accent-500", "bg-success-500", "bg-success-500"];
  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i < score ? colors[score] : "bg-white/10"}`} />
        ))}
      </div>
      {issues.length > 0 && (
        <ul className="text-[11px] text-gray-400 space-y-0.5">
          {issues.map((k) => (
            <li key={k}>• {t(k)}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Inscription e-mail + mot de passe. Sert aussi à transformer un compte invité en
 * compte réel (mode `upgrade`, progression conservée).
 */
const SignUp: React.FC<{ upgrade?: boolean; onDone?: () => void }> = ({ upgrade, onDone }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [accepted, setAccepted] = useState(Boolean(upgrade));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (upgrade) setAccepted(true);
  }, [upgrade]);

  const submit = async () => {
    if (loading) return;
    if (!isFirebaseConfigured) return setError(t("auth.errors.notConfigured"));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError(t("auth.errors.invalidEmail"));
    const issues = passwordIssues(password);
    if (issues.length) return setError(t("auth.pw.policy"));
    if (password !== repeat) return setError(t("auth.errors.passwordMismatch"));
    if (!accepted) return setError(t("auth.signup.mustAccept"));
    setLoading(true);
    try {
      if (await isPasswordPwned(password)) {
        setError(t("auth.pw.pwned"));
        return;
      }
      if (upgrade) {
        await upgradeGuestWithEmail(email, password);
        onDone && onDone();
        return;
      }
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      track("signup_completed", { method: "email" });
      if (usingEmulators) return; // le guard redirige vers /profile-setup (pas d'e-mail en local)
      await sendEmailVerification(cred.user);
      history.push("/email-verification");
    } catch (e: any) {
      setError(friendlyAuthError(e?.code, t));
    } finally {
      setLoading(false);
    }
  };

  const form = (
    <div className="space-y-4">
      <IonLoading isOpen={loading} message={t("common.loading")} />
      <div className="space-y-2">
        <label className={labelClass}>
          <i className="fa-solid fa-envelope text-primary-400" />
          {t("auth.email")}
        </label>
        <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="prenom@exemple.ch" className={inputClass} />
      </div>
      <div className="space-y-2">
        <label className={labelClass}>
          <i className="fa-solid fa-lock text-primary-400" />
          {t("auth.password")}
        </label>
        <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.signup.passwordHint")} className={inputClass} />
        <PasswordStrength password={password} />
      </div>
      <div className="space-y-2">
        <label className={labelClass}>
          <i className="fa-solid fa-lock text-primary-400" />
          {t("auth.signup.repeat")}
        </label>
        <input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} placeholder="••••••••" className={inputClass} />
      </div>
      {!upgrade && (
        <label className="flex items-start gap-3 text-xs text-gray-300 cursor-pointer">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5 w-4 h-4 accent-primary-500" />
          <span>{t("auth.signup.consent")}</span>
        </label>
      )}
      <button type="button" onClick={submit} className="btn-primary w-full text-base uppercase tracking-wide">
        {upgrade ? t("auth.guest.createWithEmail") : t("auth.signup.button")}
      </button>
      {!upgrade && (
        <p className="text-xs text-gray-400 text-center">
          {t("auth.signup.hasAccount")}{" "}
          <button type="button" onClick={() => history.push("/login")} className="text-success-400 font-semibold">
            {t("auth.signup.login")}
          </button>
        </p>
      )}
    </div>
  );

  if (upgrade) {
    return (
      <>
        {error && (
          <div className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2 mb-3" onClick={() => setError(null)}>
            {error}
          </div>
        )}
        {form}
      </>
    );
  }

  return (
    <AuthShell
      title={t("auth.signup.title")}
      subtitle={t("auth.signup.subtitle")}
      icon="fa-user-plus"
      onBack={() => history.goBack()}
      backLabel={t("common.back")}
      alert={{ open: Boolean(error), header: t("common.information"), message: error || "", onClose: () => setError(null), dismissLabel: t("common.close") }}>
      {form}
    </AuthShell>
  );
};

export default SignUp;
